import { useConversationMessageMediaContentState } from '@modules/conversation/ConversationMediaContentView/useConversationMessageMediaContent';
import { useInfiniteQuery, useQueryClient } from '@tanstack/react-query';
import { isBefore, isSameDay, parseISO } from 'date-fns';
import { useMemo } from 'react';
import { ChatMessageListItem, IChatMessage } from 'src/@types/chat';
import { ConversationMessageContentType } from 'src/@types/conversation';
import { ChatRequests } from 'src/requests/chat';
import { InfiniteData } from 'src/requests/todos';
import { useChatConversationContext } from 'src/sections/@dashboard/chat/ChatConversation/useChatConversationContext';
import { useUpdateMessage } from 'src/sections/@dashboard/chat/ChatConversation/useUpdateMessage';
import {
  onChatNewMessage,
  onDeleteMessage,
  onUpdateMessage,
} from 'src/socket/listeners/chat';
import { useSocketEvent } from 'src/socket/use-event';

const MAX_MESSAGES_PAGE = 50;

const getSameDateAndAuthor = (
  message: IChatMessage,
  previousMessage: IChatMessage,
) => {
  const createdAt = parseISO(message.createdAt);
  const isSameDayAsPreviousMessage =
    previousMessage &&
    isSameDay(parseISO(previousMessage?.createdAt), createdAt);

  const isSameAuthorAsPreviousMessage =
    isSameDayAsPreviousMessage && previousMessage?.author === message.author;

  return {
    ...message,
    isSameDayAsPreviousMessage,
    isSameAuthorAsPreviousMessage,
  };
};

function uniqByKeepLast(
  a: ChatMessageListItem[],
  key: (message: ChatMessageListItem) => string,
) {
  return [...new Map(a.map((x) => [key(x), x])).values()];
}

type Props = {
  channelId: string;
};

export const useMessages = ({ channelId }: Props) => {
  const { goToMessage, loadGoTo } = useChatConversationContext();

  const { updateMessage } = useUpdateMessage({ channelId, goToMessage });
  const { checkInitialMediaContentDownload } =
    useConversationMessageMediaContentState();

  const queryClient = useQueryClient();

  const goToMessageId = loadGoTo ? goToMessage : undefined;

  const queryKey = ['channels', channelId, goToMessageId];

  const {
    data,
    error,
    fetchNextPage,
    fetchPreviousPage,
    hasNextPage,
    hasPreviousPage,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    status,
    dataUpdatedAt,
    refetch,
  } = useInfiniteQuery({
    enabled: !!channelId,
    queryKey,
    queryFn: ({ pageParam = { around: goToMessageId } }) =>
      ChatRequests.getChannelMessages(channelId, pageParam),
    getNextPageParam: (lastPage) =>
      lastPage.length >= MAX_MESSAGES_PAGE
        ? { before: lastPage?.at(-1)?._id }
        : false,
    getPreviousPageParam: (firstPage) =>
      firstPage.length >= MAX_MESSAGES_PAGE
        ? { after: firstPage?.[0]?._id }
        : false,
    refetchOnWindowFocus: false,
    // refetchInterval: 1000 * 30,
  });

  useSocketEvent(
    onChatNewMessage((message) => {
      if (message.channel !== channelId) return;

      // PRELOAD MEDIA

      if (
        message.contentType === ConversationMessageContentType.VIDEO &&
        !!message.mediaThumbnailRef
      ) {
        checkInitialMediaContentDownload(message, 'mediaThumbnailRef');
      }

      checkInitialMediaContentDownload(message);

      queryClient.setQueryData<typeof data>(
        queryKey,
        (cache) => {
          if (!cache)
            return {
              pageParams: [undefined],
              pages: [[message]],
            };

          for (let i = 0; i < cache.pages.length; i += 1) {
            const list = cache.pages[i];

            for (let j = 0; j < list.length; j += 1) {
              if (list[j]._id === message._id) return { ...cache };

              if (
                isBefore(
                  parseISO(list[j].createdAt),
                  parseISO(message.createdAt),
                )
              ) {
                const previousMessage = list[j];
                const newMessage = getSameDateAndAuthor(
                  message,
                  previousMessage,
                );

                list.splice(j, 0, newMessage);
                return { ...cache };
              }
            }
          }

          cache.pages.at(-1)?.unshift(message);

          return { ...cache };
        },
        {
          updatedAt: Date.now(),
        },
      );
    }),
  );

  useSocketEvent(
    onUpdateMessage((message) => {
      if (message.channel !== channelId) return;
      updateMessage(message);
    }),
  );

  // TODO -> Ver se vamos remover a mensagem ou mostrar como mensagem excluída.
  // Atualmente estou removendo a mensagem.
  useSocketEvent(
    onDeleteMessage((message) => {
      if (message.channel !== channelId) return;
      queryClient.setQueryData<InfiniteData<ChatMessageListItem[]>>(
        queryKey,
        (messages) =>
          messages
            ? {
                ...messages,
                pages: [
                  ...messages.pages.map((page) => [
                    ...page.filter((item) => item._id !== message._id),
                  ]),
                ],
              }
            : messages,
        {
          updatedAt: Date.now(),
        },
      );
    }),
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: <explanation>
  const messages = useMemo(() => {
    if (!data) return [];
    const items: ChatMessageListItem[] = [];

    for (let i = data.pages.length - 1; i >= 0; i -= 1) {
      const currentPage = data?.pages[i];
      for (let j = currentPage.length - 1; j >= 0; j -= 1) {
        const message = currentPage[j];
        const previousMessage = currentPage[j + 1];

        const updatedMessage = getSameDateAndAuthor(message, previousMessage);

        items.push(updatedMessage);

        if (
          updatedMessage.contentType === ConversationMessageContentType.VIDEO &&
          !!updatedMessage.mediaThumbnailRef
        ) {
          checkInitialMediaContentDownload(updatedMessage, 'mediaThumbnailRef');
        }

        checkInitialMediaContentDownload(updatedMessage);
      }
    }

    return uniqByKeepLast(items, (message: ChatMessageListItem) => message._id);
    // dataUpdatedAt is here to force re-render when cache is updated
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data, dataUpdatedAt]);

  return {
    messages,
    isFetching,
    isFetchingNextPage,
    isFetchingPreviousPage,
    error,
    status,
    fetchNextPage,
    hasNextPage,
    fetchPreviousPage,
    hasPreviousPage,
    refetch,
  };
};
